import React, { useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAddresses } from "../hooks/use-addresses";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import {
  useCountries,
  useCities,
  useTowns,
  useQuarters,
} from "@/api/hooks/use-query-api";
import { LoadingSpinner } from "@/components/common/loading-spinner";

// Address form schema
const addressSchema = z.object({
  label: z.string().min(1, "Address title is required"),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  addressLine1: z.string().min(1, "Address is required"),
  addressLine2: z.string().optional(),
  city: z.string().min(1, "City is required"),
  cityId: z.number().optional(),
  state: z.string().min(1, "State is required"),
  townId: z.number().optional(),
  quarter: z.string().optional(),
  quarterId: z.string().optional(),
  postalCode: z.string().min(1, "Postal code is required"),
  country: z.string().min(1, "Country is required"),
  countryId: z.number().optional(),
});

type AddressFormData = z.infer<typeof addressSchema>;

const emptyFormValues: AddressFormData = {
  label: "",
  firstName: "",
  lastName: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  cityId: undefined,
  state: "",
  townId: undefined,
  quarter: "",
  quarterId: undefined,
  postalCode: "",
  country: "",
  countryId: undefined,
};

export const AddAddressDialog: React.FC = () => {
  const { t } = useTranslation();
  const {
    isEditAddressDialogOpen,
    closeEditAddressDialog,
    editingAddress,
    saveAddress,
    isSaving,
  } = useAddresses();

  // Location API Hooks
  const { data: countriesData, isLoading: isLoadingCountries } = useCountries();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<AddressFormData>({
    resolver: zodResolver(addressSchema),
    defaultValues: emptyFormValues,
  });

  // Watch form values for cascade selection
  const selectedCountryId = watch("countryId");
  const selectedCityId = watch("cityId");
  const selectedTownId = watch("townId");
  const selectedQuarterId = watch("quarterId");

  // Track which select is open - only one can be open at a time
  const [openSelect, setOpenSelect] = React.useState<string | null>(null);

  // Close other selects when one opens
  const handleSelectOpenChange = (selectName: string, open: boolean) => {
    if (open) {
      // Close all other selects first
      if (openSelect && openSelect !== selectName) {
        // Find and close the other open select by clicking outside
        setTimeout(() => {
          const otherSelects = ["country", "city", "state", "quarter"].filter(
            (name) => name !== selectName && name === openSelect
          );
          otherSelects.forEach((name) => {
            const trigger = document.querySelector(
              `[data-select-name="${name}"]`
            ) as HTMLElement;
            if (trigger && trigger.getAttribute("aria-expanded") === "true") {
              // Click outside to close
              const clickEvent = new MouseEvent("mousedown", {
                bubbles: true,
                cancelable: true,
                view: window,
              });
              document.body.dispatchEvent(clickEvent);
            }
          });
        }, 10);
      }
      setOpenSelect(selectName);
    } else {
      if (openSelect === selectName) {
        setOpenSelect(null);
      }
    }
  };

  // Fetch cities when country is selected
  const { data: citiesData, isLoading: isLoadingCities } = useCities(
    selectedCountryId || null
  );

  // Fetch towns when city is selected
  const { data: townsData, isLoading: isLoadingTowns } = useTowns(
    selectedCountryId || null,
    selectedCityId || null
  );

  // Fetch quarters when town is selected
  const { data: quartersData, isLoading: isLoadingQuarters } = useQuarters(
    selectedCountryId || null,
    selectedCityId || null,
    selectedTownId || null
  );

  // Prepare countries list
  const countries = useMemo(() => {
    if (!countriesData?.data) return [];
    return countriesData.data.map((country) => ({
      value: country.countryId.toString(),
      label: country.name,
      countryId: country.countryId,
    }));
  }, [countriesData]);

  // Prepare cities list
  const cities = useMemo(() => {
    if (!citiesData?.data) return [];
    return citiesData.data.map((city) => ({
      value: city.cityId.toString(),
      label: city.name,
      cityId: city.cityId,
    }));
  }, [citiesData]);

  // Prepare towns list
  const towns = useMemo(() => {
    if (!townsData?.data) return [];
    return townsData.data.map((town) => ({
      value: town.townId.toString(),
      label: town.name,
      townId: town.townId,
    }));
  }, [townsData]);

  // Prepare quarters list
  const quarters = useMemo(() => {
    if (!quartersData?.data) return [];
    return quartersData.data.map((quarter) => ({
      value: quarter._id,
      label: quarter.name,
      quarterId: quarter._id,
    }));
  }, [quartersData]);

  // Handle country change - reset dependent fields
  const handleCountryChange = (value: string) => {
    const country = countries.find((c) => c.value === value);
    if (country) {
      setValue("countryId", country.countryId);
      setValue("country", country.label);
      // Reset dependent fields
      setValue("city", "");
      setValue("cityId", undefined);
      setValue("state", "");
      setValue("townId", undefined);
      setValue("quarter", "");
      setValue("quarterId", undefined);
    }
  };

  // Handle city change - reset dependent fields
  const handleCityChange = (value: string) => {
    const city = cities.find((c) => c.value === value);
    if (city) {
      setValue("cityId", city.cityId);
      setValue("city", city.label);
      // Reset dependent fields
      setValue("state", "");
      setValue("townId", undefined);
      setValue("quarter", "");
      setValue("quarterId", undefined);
    }
  };

  // Handle town change - reset dependent fields
  const handleTownChange = (value: string) => {
    const town = towns.find((t) => t.value === value);
    if (town) {
      setValue("townId", town.townId);
      setValue("state", town.label);
      // Reset dependent fields
      setValue("quarter", "");
      setValue("quarterId", undefined);
    }
  };

  // Handle quarter change
  const handleQuarterChange = (value: string) => {
    const quarter = quarters.find((q) => q.value === value);
    if (quarter) {
      setValue("quarterId", quarter.quarterId);
      setValue("quarter", quarter.label);
    }
  };

  // Check if cities are available for selected country
  const hasCities = cities.length > 0;
  const hasTowns = towns.length > 0;
  const hasQuarters = quarters.length > 0;

  // Form'u editingAddress değiştiğinde güncelle
  useEffect(() => {
    if (isEditAddressDialogOpen) {
      if (editingAddress) {
        // Edit mode - form'u address data ile doldur
        reset({
          label: editingAddress.label || "",
          firstName: editingAddress.firstName || "",
          lastName: editingAddress.lastName || "",
          addressLine1: editingAddress.addressLine1 || "",
          addressLine2: editingAddress.addressLine2 || "",
          city: editingAddress.city || "",
          cityId: undefined, // Will be set when country/city is selected
          state: editingAddress.state || "",
          townId: undefined, // Will be set when city/town is selected
          quarter: (editingAddress as any).quarter || "",
          quarterId: undefined,
          postalCode: editingAddress.postalCode || "",
          country: editingAddress.country || "",
          countryId: undefined, // Will be set when country is selected
        });
      } else {
        // Add mode - form'u temizle
        reset(emptyFormValues);
      }
    }
  }, [isEditAddressDialogOpen, editingAddress, reset]);

  // Set countryId when countries data is loaded and editingAddress has country
  useEffect(() => {
    if (editingAddress && countries.length > 0 && !selectedCountryId) {
      const country = countries.find(
        (c) =>
          c.label === editingAddress.country ||
          c.label.toUpperCase() === editingAddress.country?.toUpperCase()
      );
      if (country) {
        setValue("countryId", country.countryId);
        setValue("country", country.label);
      }
    }
  }, [countries, editingAddress, selectedCountryId, setValue]);

  // Set cityId when cities data is loaded and editingAddress has city
  useEffect(() => {
    if (
      editingAddress &&
      cities.length > 0 &&
      selectedCountryId &&
      !selectedCityId
    ) {
      const city = cities.find(
        (c) =>
          c.label === editingAddress.city ||
          c.label.toUpperCase() === editingAddress.city?.toUpperCase()
      );
      if (city) {
        setValue("cityId", city.cityId);
        setValue("city", city.label);
      }
    }
  }, [cities, editingAddress, selectedCountryId, selectedCityId, setValue]);

  // Set townId when towns data is loaded and editingAddress has state
  useEffect(() => {
    if (
      editingAddress &&
      towns.length > 0 &&
      selectedCityId &&
      !selectedTownId
    ) {
      const town = towns.find(
        (t) =>
          t.label === editingAddress.state ||
          t.label.toUpperCase() === editingAddress.state?.toUpperCase()
      );
      if (town) {
        setValue("townId", town.townId);
        setValue("state", town.label);
      }
    }
  }, [towns, editingAddress, selectedCityId, selectedTownId, setValue]);

  // Set quarterId when quarters data is loaded and editingAddress has quarter
  useEffect(() => {
    if (
      editingAddress &&
      quarters.length > 0 &&
      selectedTownId &&
      !selectedQuarterId
    ) {
      const quarter = quarters.find(
        (q) =>
          q.label === (editingAddress as any).quarter ||
          q.label.toUpperCase() ===
            (editingAddress as any).quarter?.toUpperCase()
      );
      if (quarter) {
        setValue("quarterId", quarter.quarterId);
        setValue("quarter", quarter.label);
      }
    }
  }, [quarters, editingAddress, selectedTownId, selectedQuarterId, setValue]);

  const onSubmit = async (data: AddressFormData) => {
    await saveAddress(data, editingAddress?._id);
  };

  const handleClose = () => {
    closeEditAddressDialog();
    reset(emptyFormValues);
  };

  // Handle ESC key
  React.useEffect(() => {
    if (!isEditAddressDialogOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isSaving) {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isEditAddressDialogOpen, isSaving]);

  // Prevent body scroll when modal is open
  React.useEffect(() => {
    if (isEditAddressDialogOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isEditAddressDialogOpen]);

  if (!isEditAddressDialogOpen) return null;

  return createPortal(
    <AnimatePresence>
      {isEditAddressDialogOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[1100] bg-black/30 backdrop-blur-sm"
            onClick={handleClose}
          />

          {/* Content */}
          <div className="fixed inset-0 z-[1100] flex items-center justify-center px-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{
                type: "spring",
                damping: 25,
                stiffness: 300,
              }}
              className="max-w-2xl w-full p-6 rounded-[20px] bg-white dark:bg-gray-900 shadow-lg pointer-events-auto max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold leading-[28px] text-text-primary dark:text-text-primary">
                  {editingAddress
                    ? t("addresses.editAddress")
                    : t("addresses.addAddress")}
                </h2>
                <button
                  onClick={handleClose}
                  className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  disabled={isSaving}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {/* First Name and Last Name */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label
                      className="text-text-secondary dark:text-text-primary"
                      htmlFor="firstName"
                    >
                      {t("addresses.firstName")}
                    </Label>
                    <Input
                      id="firstName"
                      placeholder={t("addresses.firstNamePlaceholder")}
                      {...register("firstName")}
                      className={errors.firstName ? "border-destructive" : ""}
                    />
                    {errors.firstName && (
                      <p className="text-sm text-destructive">
                        {errors.firstName.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label
                      className="text-text-secondary dark:text-text-primary"
                      htmlFor="lastName"
                    >
                      {t("addresses.lastName")}
                    </Label>
                    <Input
                      id="lastName"
                      placeholder={t("addresses.lastNamePlaceholder")}
                      {...register("lastName")}
                      className={errors.lastName ? "border-destructive" : ""}
                    />
                    {errors.lastName && (
                      <p className="text-sm text-destructive">
                        {errors.lastName.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* Address Title */}
                <div className="space-y-2">
                  <Label
                    className="text-text-secondary dark:text-text-primary"
                    htmlFor="label"
                  >
                    {t("addresses.addressTitle")}
                  </Label>
                  <Input
                    id="label"
                    placeholder={t("addresses.addressTitlePlaceholder")}
                    {...register("label")}
                    className={errors.label ? "border-destructive" : ""}
                  />
                  {errors.label && (
                    <p className="text-sm text-destructive">
                      {errors.label.message}
                    </p>
                  )}
                </div>

                {/* Country, City, State, Quarter - Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* Country */}
                  <div className="space-y-2">
                    <Label
                      className="text-text-secondary dark:text-text-primary"
                      htmlFor="country"
                    >
                      {t("addresses.country")}
                    </Label>
                    {isLoadingCountries ? (
                      <div className="flex items-center justify-center py-2">
                        <LoadingSpinner size="sm" />
                      </div>
                    ) : (
                      <Select
                        value={selectedCountryId?.toString() || ""}
                        onValueChange={handleCountryChange}
                        onOpenChange={(open) =>
                          handleSelectOpenChange("country", open)
                        }
                      >
                        <SelectTrigger
                          className="w-full"
                          data-select-name="country"
                        >
                          <SelectValue
                            placeholder={t("addresses.countryPlaceholder")}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map((country) => (
                            <SelectItem
                              key={country.value}
                              value={country.value}
                            >
                              {country.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {errors.country && (
                      <p className="text-sm text-destructive">
                        {errors.country.message}
                      </p>
                    )}
                  </div>

                  {/* City */}
                  {selectedCountryId && (
                    <div className="space-y-2">
                      <Label
                        className="text-text-secondary dark:text-text-primary"
                        htmlFor="city"
                      >
                        {t("addresses.city")}
                      </Label>
                      {isLoadingCities ? (
                        <div className="flex items-center justify-center py-2">
                          <LoadingSpinner size="sm" />
                        </div>
                      ) : hasCities ? (
                        <Select
                          value={selectedCityId?.toString() || ""}
                          onValueChange={handleCityChange}
                          onOpenChange={(open) =>
                            handleSelectOpenChange("city", open)
                          }
                        >
                          <SelectTrigger
                            className="w-full bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                            data-select-name="city"
                          >
                            <SelectValue
                              placeholder={t("addresses.cityPlaceholder")}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {cities.map((city) => (
                              <SelectItem key={city.value} value={city.value}>
                                {city.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          id="city"
                          placeholder={t("addresses.cityPlaceholder")}
                          {...register("city")}
                          className={errors.city ? "border-destructive" : ""}
                        />
                      )}
                      {errors.city && (
                        <p className="text-sm text-destructive">
                          {errors.city.message}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Town/State */}
                  {selectedCityId && (
                    <div className="space-y-2">
                      <Label
                        className="text-text-secondary dark:text-text-primary"
                        htmlFor="state"
                      >
                        {t("addresses.state")}
                      </Label>
                      {isLoadingTowns ? (
                        <div className="flex items-center justify-center py-2">
                          <LoadingSpinner size="sm" />
                        </div>
                      ) : hasTowns ? (
                        <Select
                          value={selectedTownId?.toString() || ""}
                          onValueChange={handleTownChange}
                          onOpenChange={(open) =>
                            handleSelectOpenChange("state", open)
                          }
                        >
                          <SelectTrigger
                            className="w-full bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                            data-select-name="state"
                          >
                            <SelectValue
                              placeholder={t("addresses.statePlaceholder")}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {towns.map((town) => (
                              <SelectItem key={town.value} value={town.value}>
                                {town.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          id="state"
                          placeholder={t("addresses.statePlaceholder")}
                          {...register("state")}
                          className={errors.state ? "border-destructive" : ""}
                        />
                      )}
                      {errors.state && (
                        <p className="text-sm text-destructive">
                          {errors.state.message}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Quarter (Optional) */}
                  {selectedTownId && hasQuarters && (
                    <div className="space-y-2">
                      <Label
                        className="text-text-secondary dark:text-text-primary"
                        htmlFor="quarter"
                      >
                        {t("addresses.quarter")}{" "}
                        <span className="text-muted-foreground">
                          ({t("addresses.optional")})
                        </span>
                      </Label>
                      {isLoadingQuarters ? (
                        <div className="flex items-center justify-center py-2">
                          <LoadingSpinner size="sm" />
                        </div>
                      ) : (
                        <Select
                          value={selectedQuarterId || ""}
                          onValueChange={handleQuarterChange}
                          onOpenChange={(open) =>
                            handleSelectOpenChange("quarter", open)
                          }
                        >
                          <SelectTrigger
                            className="w-full bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                            data-select-name="quarter"
                          >
                            <SelectValue
                              placeholder={t("addresses.selectQuarter")}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {quarters.map((quarter) => (
                              <SelectItem
                                key={quarter.value}
                                value={quarter.value}
                              >
                                {quarter.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  )}
                </div>

                {/* ZIP Code */}
                <div className="space-y-2">
                  <Label
                    className="text-text-secondary dark:text-text-primary"
                    htmlFor="postalCode"
                  >
                    {t("addresses.zipCode")}
                  </Label>
                  <Input
                    id="postalCode"
                    placeholder="94025"
                    {...register("postalCode")}
                    className={errors.postalCode ? "border-destructive" : ""}
                  />
                  {errors.postalCode && (
                    <p className="text-sm text-destructive">
                      {errors.postalCode.message}
                    </p>
                  )}
                </div>

                {/* Address Line 1 */}
                <div className="space-y-2">
                  <Label
                    className="text-text-secondary dark:text-text-primary"
                    htmlFor="addressLine1"
                  >
                    {t("addresses.address")}
                  </Label>
                  <Input
                    id="addressLine1"
                    placeholder={t("addresses.addressPlaceholder")}
                    {...register("addressLine1")}
                    className={errors.addressLine1 ? "border-destructive" : ""}
                  />
                  {errors.addressLine1 && (
                    <p className="text-sm text-destructive">
                      {errors.addressLine1.message}
                    </p>
                  )}
                </div>

                {/* Address Line 2 (Optional) */}
                <div className="space-y-2">
                  <button
                    type="button"
                    className="text-xs md:text-sm text-primary hover:underline"
                    onClick={() => {
                      const input = document.getElementById(
                        "addressLine2"
                      ) as HTMLInputElement;
                      input?.focus();
                    }}
                  >
                    + {t("addresses.addApartmentSuite")}
                  </button>
                  <Input
                    id="addressLine2"
                    placeholder={t("addresses.apartmentSuitePlaceholder")}
                    {...register("addressLine2")}
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    disabled={isSaving}
                    className="flex-1 text-text-secondary dark:text-text-primary"
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    variant="darkPrimary"
                    disabled={isSaving}
                    className="flex-1"
                  >
                    {isSaving
                      ? t("common.saving")
                      : editingAddress
                      ? t("common.update")
                      : t("common.save")}
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};
